import { activeSiteConfig } from "@config/site/site-config";
import type { ProductPrice } from "@graphql/generated/components";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CustomAttributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { Text } from "@ui/components/content/text/Text";
import { Money } from "@ui/components/data-display/money/Money";
import type { SimpleMoneyData } from "@ui/components/data-display/money/useMoney";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { useRouter } from "next/router";
import { discountStyle, priceBlockStyle, priceStyle } from "./ProductBuyCardPrice.styles";

interface ProductBuyCardPriceProps {
    price: ProductPrice;
}

export const DiscountedPriceBlock = ({
    label,
    price,
    dataTestId,
    locale,
    isOmnibusMarket,
    discount,
}: {
    label: string;
    price: SimpleMoneyData;
    dataTestId: string;
    locale: string;
    isOmnibusMarket?: boolean;
    discount?: number;
}) => (
    <Flex sx={priceBlockStyle}>
        <Text sx={priceStyle}>{label}</Text>
        <Money
            textDecoration="line-through"
            locale={locale}
            data-testid={dataTestId}
            data={price}
        />
        {discount > 0 && isOmnibusMarket && <Text sx={discountStyle}>{`-${discount}%`}</Text>}
    </Flex>
);

const ProductBuyCardPrice = ({ price }: ProductBuyCardPriceProps) => {
    const { locale } = useRouter();
    const { getMultiple } = useMicrocopy();
    const isOmnibusMarket = JSON.parse(process.env.NEXT_PUBLIC_OMNIBUS_MARKET ?? "false");

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.price.recommendedPrice,
        CommonCMS.price.previousLowestPrice,
        CommonCMS.price.adjustedPrice,
        CommonCMS.price.includesTaxes,
    ]);

    const customFields = price?.custom?.customFieldsRaw;
    const recommendedPrice = getProductAttributeValue(
        customFields,
        CustomAttributes.RECOMMENDED_PRICE
    ) as SimpleMoneyData;
    const discountPercentage = getProductAttributeValue(
        customFields,
        CustomAttributes.DISCOUNT_PERCENTAGE
    );
    const previousLowestPrice = getProductAttributeValue(
        customFields,
        CustomAttributes.PREVIOUS_LOWEST_PRICE
    ) as SimpleMoneyData;
    const discountPercentagePreviousLowestPrice = getProductAttributeValue(
        customFields,
        CustomAttributes.DISCOUNT_PERCENTAGE_PREVIOUS_LOWEST_PRICE
    );
    const adjustedPrice = getProductAttributeValue(
        customFields,
        CustomAttributes.ADJUSTED_PRICE
    ) as SimpleMoneyData;

    const showAdjustedPrice = Boolean(
        adjustedPrice?.centAmount &&
            adjustedPrice.centAmount !== price?.value?.centAmount &&
            adjustedPrice.centAmount !== previousLowestPrice?.centAmount
    );
    const showPreviousLowestPrice = Boolean(
        previousLowestPrice?.centAmount &&
            discountPercentagePreviousLowestPrice &&
            discountPercentage > 0 &&
            recommendedPrice.centAmount !== previousLowestPrice?.centAmount
    );
    const showRecommendedPrice = Boolean(recommendedPrice?.centAmount && discountPercentage > 0);

    return (
        <Box mt={1}>
            <Flex flexDirection={"column"} gap={2}>
                <Flex gap={2} alignItems={"center"}>
                    <Money
                        fontWeight="bold"
                        locale={locale}
                        data={price?.value}
                        color={discountPercentage > 0 && isOmnibusMarket ? "red.100" : "black"}
                        data-testid="SelectedVariantprice"
                        fontSize={{
                            base: "mobileHeading2",
                            lg: "desktopHeading2",
                        }}
                    />
                </Flex>
                <Flex gap={2} flexDirection="column">
                    {showPreviousLowestPrice && (
                        <DiscountedPriceBlock
                            label={microcopies[CommonCMS.price.previousLowestPrice]}
                            price={previousLowestPrice}
                            dataTestId="PreviousLowestPrice"
                            locale={locale}
                            isOmnibusMarket={isOmnibusMarket}
                            discount={discountPercentagePreviousLowestPrice}
                        />
                    )}
                    {showRecommendedPrice && (
                        <DiscountedPriceBlock
                            label={microcopies[CommonCMS.price.recommendedPrice]}
                            price={recommendedPrice}
                            dataTestId="RecommendedPrice"
                            locale={locale}
                            isOmnibusMarket={isOmnibusMarket}
                            discount={discountPercentage}
                        />
                    )}
                    {showAdjustedPrice && (
                        <DiscountedPriceBlock
                            label={microcopies[CommonCMS.price.adjustedPrice]}
                            price={adjustedPrice}
                            dataTestId="AdjustedPrice"
                            locale={locale}
                        />
                    )}
                    {activeSiteConfig?.showPriceLegalText && (
                        <Text sx={priceStyle}>{microcopies[CommonCMS.price.includesTaxes]}</Text>
                    )}
                </Flex>
            </Flex>
        </Box>
    );
};

export default ProductBuyCardPrice;
